// web/src/services/appointmentsService.js
import apiClient from './apiClient';
import toast from 'react-hot-toast';

/**
 * Get appointments for user (patient or physician)
 */
export const getAppointmentsForUser = async () => {
  try {
    // Backend automatically filters by role: patients see their appointments,
    // providers see theirs. No need to pass IDs — avoids broken AND filter.
    const data = await apiClient.get('/appointments', {
      limit: 100,
      sortBy: 'scheduled_time',
      sortOrder: 'desc'
    });
    return { data, error: null };
  } catch (error) {
    // Suppress connection errors
    const errorMessage = error?.message || error?.toString() || '';
    const isConnectionError = error?.isConnectionError ||
      errorMessage.includes('Backend server is not available') ||
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('ERR_CONNECTION_REFUSED');

    // Only log and show toast for non-connection errors
    if (!isConnectionError) {
      if (import.meta.env.DEV) {
        console.error('Error fetching appointments:', error);
      }
      toast.error(error.message || 'Failed to fetch appointments');
    }
    return { data: [], error: isConnectionError ? null : error };
  }
};

/**
 * Get appointment statistics for user
 */
export const getAppointmentStats = async () => {
  try {
    const stats = await apiClient.get('/appointments/stats');
    return { data: stats, error: null };
  } catch (error) {
    // Suppress connection errors (expected when backend is down)
    const errorMessage = error?.message || error?.toString() || '';
    const isConnectionError = error?.isConnectionError ||
      errorMessage.includes('Backend server is not available') ||
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('ERR_CONNECTION_REFUSED') ||
      (error instanceof TypeError && errorMessage.includes('fetch'));

    // Silently handle connection errors
    if (!isConnectionError && import.meta.env.DEV) {
      console.warn('Error fetching appointment stats:', errorMessage);
    }
    // Return default stats on error
    return { data: { total: 0, upcoming: 0, past: 0 }, error: null };
  }
};

/**
 * Get upcoming appointments for user
 */
export const getUpcomingAppointments = async (userId, limit = 5) => {
  try {
    const response = await apiClient.getUpcomingAppointments(limit);

    // Handle different response formats
    let appointments = [];
    if (Array.isArray(response)) {
      appointments = response;
    } else if (response?.data && Array.isArray(response.data)) {
      appointments = response.data;
    } else if (response?.rows && Array.isArray(response.rows)) {
      appointments = response.rows;
    }

    if (!appointments || appointments.length === 0) {
      return { data: [], error: null };
    }

    // Extract unique provider IDs (for patients) or patient IDs (for physicians)
    const providerIds = [...new Set(appointments
      .map(appt => appt.provider_id || appt.physician_id)
      .filter(Boolean)
    )];

    // Fetch physician details by looking up each provider directly
    let physicianMap = {};
    if (providerIds.length > 0) {
      try {
        const results = await Promise.allSettled(
          providerIds.map(id => apiClient.getUserById(id))
        );
        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) {
            const user = result.value;
            physicianMap[user.id] = user;
          }
        });
      } catch (fetchError) {
        // Silently handle errors fetching physician details
        if (import.meta.env.DEV) {
          console.warn('Error fetching physician details:', fetchError);
        }
      }
    }

    // Format for display
    const formattedData = appointments
      .filter(appt => {
        // Filter out appointments with invalid dates
        const scheduledTime = appt.scheduled_time || appt.scheduled_at || appt.appointment_time;
        if (!scheduledTime) return false;
        const date = new Date(scheduledTime);
        return !isNaN(date.getTime()) && date.getTime() > 0;
      })
      .map(appt => {
        const scheduledTime = appt.scheduled_time || appt.scheduled_at || appt.appointment_time;
        const providerId = appt.provider_id || appt.physician_id;
        const physician = providerId ? physicianMap[providerId] : null;

        // Format date safely with better formatting
        let dateStr = 'Date TBD';
        let timeStr = 'Time TBD';
        if (scheduledTime) {
          try {
            const date = new Date(scheduledTime);
            // Double-check date is valid and not epoch
            if (!isNaN(date.getTime()) && date.getTime() > 0) {
              // Check it's not epoch (Jan 1, 1970 or before)
              const epochDate = new Date(0);
              if (date.getTime() > epochDate.getTime()) {
                // Format date: "Nov 20, 2025"
                dateStr = date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                });
                // Format time: "2:00 PM"
                timeStr = date.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
              } else {
                // Invalid date (epoch or before)
                dateStr = 'Date TBD';
                timeStr = 'Time TBD';
              }
            }
          } catch (e) {
            // Invalid date, use defaults
            if (import.meta.env.DEV) {
              console.warn('Invalid date format for appointment:', appt.id, scheduledTime);
            }
          }
        }

        return {
          id: appt.id,
          doctor: physician
            ? `Dr. ${physician.first_name || physician.firstName || ''} ${physician.last_name || physician.lastName || ''}`.trim() || 'Unknown Physician'
            : 'Unknown Physician',
          specialty: physician?.specialty || 'General',
          date: dateStr,
          time: timeStr,
          type: appt.appointment_type || appt.type || 'Video Consultation',
          status: appt.status
        };
      });

    return { data: formattedData, error: null };
  } catch (error) {
    // Suppress connection errors (expected when backend is down)
    const errorMessage = error?.message || error?.toString() || '';
    const isConnectionError = error?.isConnectionError ||
      errorMessage.includes('Backend server is not available') ||
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('ERR_CONNECTION_REFUSED') ||
      (error instanceof TypeError && errorMessage.includes('fetch'));

    // Silently handle connection errors
    if (!isConnectionError && import.meta.env.DEV) {
      console.warn('Error fetching upcoming appointments:', errorMessage);
    }
    return { data: [], error: null }; // Return empty array on error
  }
};

/**
 * Create new appointment
 */
export const createAppointment = async (appointmentData) => {
  try {
    const data = await apiClient.createAppointment(appointmentData);
    toast.success('Appointment created successfully');
    return { data, error: null };
  } catch (error) {
    console.error('Error creating appointment:', error);
    toast.error(error.message || 'Failed to create appointment');
    return { data: null, error };
  }
};

/**
 * Update appointment
 */
export const updateAppointment = async (appointmentId, updates) => {
  try {
    const data = await apiClient.updateAppointment(appointmentId, updates);
    toast.success('Appointment updated successfully');
    return { data, error: null };
  } catch (error) {
    console.error('Error updating appointment:', error);
    toast.error(error.message || 'Failed to update appointment');
    return { data: null, error };
  }
};

/**
 * Delete appointment
 */
export const deleteAppointment = async (appointmentId) => {
  try {
    await apiClient.deleteAppointment(appointmentId);
    toast.success('Appointment deleted successfully');
    return { data: true, error: null };
  } catch (error) {
    console.error('Error deleting appointment:', error);
    toast.error(error.message || 'Failed to delete appointment');
    return { data: null, error };
  }
};

/**
 * Approve appointment request
 */
export const approveAppointmentRequest = async (appointmentId) => {
  try {
    const data = await apiClient.patch(`/appointments/${appointmentId}/approve`, {});
    toast.success('Appointment request approved');
    return { data, error: null };
  } catch (error) {
    console.error('Error approving appointment request:', error);
    toast.error(error.message || 'Failed to approve appointment request');
    return { data: null, error };
  }
};

/**
 * Reject appointment request
 */
export const rejectAppointmentRequest = async (appointmentId) => {
  try {
    const data = await apiClient.patch(`/appointments/${appointmentId}/reject`, {});
    toast.success('Appointment request rejected');
    return { data, error: null };
  } catch (error) {
    console.error('Error rejecting appointment request:', error);
    toast.error(error.message || 'Failed to reject appointment request');
    return { data: null, error };
  }
};

/**
 * Cancel appointment with optional reason
 */
export const cancelAppointment = async (appointmentId, reason = '') => {
  try {
    // Use updateAppointment to set status to cancelled with reason
    const updates = {
      status: 'cancelled',
      ...(reason && { cancellation_reason: reason })
    };
    const data = await apiClient.updateAppointment(appointmentId, updates);
    toast.success('Appointment cancelled successfully');
    return { data, error: null };
  } catch (error) {
    console.error('Error cancelling appointment:', error);
    toast.error(error.message || 'Failed to cancel appointment');
    return { data: null, error };
  }
};

/**
 * Reschedule appointment to a new date/time
 */
export const rescheduleAppointment = async (appointmentId, newScheduledTime) => {
  try {
    const updates = {
      scheduled_time: newScheduledTime,
      // Reset status if it was cancelled
      status: 'scheduled'
    };
    const data = await apiClient.updateAppointment(appointmentId, updates);
    toast.success('Appointment rescheduled successfully');
    return { data, error: null };
  } catch (error) {
    console.error('Error rescheduling appointment:', error);
    toast.error(error.message || 'Failed to reschedule appointment');
    return { data: null, error };
  }
};